import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { usePlaidLink } from 'react-plaid-link';
import {
  CheckCircle, AlertTriangle, Clock, CreditCard, Wallet, Loader2,
  ArrowLeft, ArrowRight, User, Mail, Phone, FileText, Shield, Copy, Check,
  Building2, ChevronDown, ChevronUp, Lock
} from 'lucide-react';
import config from '../config';
import { getTenantConfig } from '../tenants';

const fmtCurrency = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((cents || 0) / 100);

interface ActiveTransaction {
  txn_id: string;
  status: string;
  method: string;
  amount_cents: number;
  created_at: string;
}

interface EngagementData {
  engagement_id: string;
  title: string;
  description: string;
  amount_cents: number;
  amount_options_cents?: number[];
  allow_multiple_payments?: boolean;
  currency: string;
  methods_accepted: string[];
  type: string;
  frequency: string;
  status: string;
  contact_name: string;
  link_expires_at: string | null;
  active_transaction?: ActiveTransaction;
  requires_signature?: boolean;
  engagement_documents?: { id: string; title: string; original_filename: string; category: string; mime_type: string; file_size_bytes: number }[];
}

type Step = 'method' | 'info' | 'details' | 'documents' | 'confirm' | 'success';

/* ----------------------------------------------------------------
   Inline Plaid Link wrapper for the public checkout page.
   Uses the public /pay/plaid-link-token and /pay/plaid-exchange
   endpoints (no auth required).
   ---------------------------------------------------------------- */
interface PlaidCheckoutProps {
  onAccountLinked: (data: {
    routing_number: string;
    account_number: string;
    account_mask: string;
    account_type: string;
    institution_name: string;
  }) => void;
  disabled?: boolean;
}

const PlaidCheckoutLink: React.FC<PlaidCheckoutProps> = ({ onAccountLinked, disabled }) => {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [exchanging, setExchanging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLinkToken = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${config.apiBaseUrl}/pay/plaid-link-token`, { method: 'POST' });
      const data = await res.json();
      if (data?.success && data?.data?.link_token) {
        setLinkToken(data.data.link_token);
      } else {
        throw new Error('Failed to get link token');
      }
    } catch (err: any) {
      setError('Could not initialize bank connection. You can enter details manually below.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleOnSuccess = useCallback(async (publicToken: string, _metadata: any) => {
    setExchanging(true);
    setError(null);
    try {
      const res = await fetch(`${config.apiBaseUrl}/pay/plaid-exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ public_token: publicToken }),
      });
      const data = await res.json();
      if (data?.success && data?.data) {
        onAccountLinked(data.data);
      } else {
        throw new Error(data?.message || 'Failed to verify account');
      }
    } catch (err: any) {
      setError(err.message || 'Bank verification failed. Please enter details manually.');
    } finally {
      setExchanging(false);
      setLinkToken(null);
    }
  }, [onAccountLinked]);

  const handleOnExit = useCallback((_err: any) => {
    setLinkToken(null);
  }, []);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: handleOnSuccess,
    onExit: handleOnExit,
  });

  // Auto-open when token arrives
  useEffect(() => {
    if (linkToken && ready) open();
  }, [linkToken, ready, open]);

  const handleClick = async () => {
    if (linkToken && ready) {
      open();
    } else {
      await fetchLinkToken();
    }
  };

  return (
    <div>
      <button
        onClick={handleClick}
        disabled={disabled || loading || exchanging}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-medium text-sm
          bg-green-600 hover:bg-green-700 text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading || exchanging ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> {exchanging ? 'Verifying Account...' : 'Connecting...'}</>
        ) : (
          <><Building2 className="h-4 w-4" /> Connect Your Bank with Plaid</>
        )}
      </button>
      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
    </div>
  );
};

/* ----------------------------------------------------------------
   Main PaymentLink page
   ---------------------------------------------------------------- */
const PaymentLink: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const tenant = getTenantConfig();
  const brandName = tenant?.branding?.name || 'REDEX';
  const logoLight = tenant?.branding?.logoLightPath || tenant?.branding?.logoPrimaryPath;

  const [loading, setLoading] = useState(true);
  const [engagement, setEngagement] = useState<EngagementData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>('method');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [txnResult, setTxnResult] = useState<any>(null);
  const [copied, setCopied] = useState(false);
  const [activeTransaction, setActiveTransaction] = useState<ActiveTransaction | null>(null);
  const [selectedAmountCents, setSelectedAmountCents] = useState<number | null>(null);

  // Form state
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [payerName, setPayerName] = useState('');
  const [payerEmail, setPayerEmail] = useState('');
  const [payerPhone, setPayerPhone] = useState('');
  const [notes, setNotes] = useState('');

  // ACH state
  const [achRouting, setAchRouting] = useState('');
  const [achAccountNumber, setAchAccountNumber] = useState('');
  const [achAccountType, setAchAccountType] = useState<'checking' | 'savings'>('checking');
  const [achLinkedViaPlaid, setAchLinkedViaPlaid] = useState(false);
  const [achInstitutionName, setAchInstitutionName] = useState('');
  const [achMask, setAchMask] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);

  // USDC state
  const [walletAddress, setWalletAddress] = useState('');
  const [chain, setChain] = useState('base');

  // Legal acceptance state — all unchecked by default (non-negotiable)
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptRiskDisclosure, setAcceptRiskDisclosure] = useState(false);
  const [acceptAchAuth, setAcceptAchAuth] = useState(false);
  const [acceptCryptoIrrevocable, setAcceptCryptoIrrevocable] = useState(false);

  // Document signature state
  const [signedDocIds, setSignedDocIds] = useState<string[]>([]);

  const amountOptionsCents = (engagement?.amount_options_cents || [])
    .map(amount => Number(amount))
    .filter(amount => Number.isInteger(amount) && amount > 0)
    .sort((left, right) => left - right);
  const hasAmountOptions = amountOptionsCents.length > 0;
  const paymentAmountCents = selectedAmountCents ?? engagement?.amount_cents ?? 0;
  const isAmountSelected = !hasAmountOptions || amountOptionsCents.includes(paymentAmountCents);

  /* --- Load engagement --- */
  useEffect(() => {
    const fetchEngagement = async () => {
      try {
        const res = await fetch(`${config.apiBaseUrl}/pay/${token}?t=${Date.now()}`, { cache: 'no-store' });
        const data = await res.json();
        if (!res.ok || !data.success) {
          setError(data.message || 'Payment link is invalid or has expired.');
          return;
        }
        const eng = data.data;
        if (typeof eng.methods_accepted === 'string') {
          try { eng.methods_accepted = JSON.parse(eng.methods_accepted); } catch { eng.methods_accepted = []; }
        }
        if (eng.active_transaction) {
          setActiveTransaction(eng.active_transaction);
        }
        setEngagement(eng);
        const options = (eng.amount_options_cents || [])
          .map((amount: any) => Number(amount))
          .filter((amount: number) => Number.isInteger(amount) && amount > 0);
        setSelectedAmountCents(options.length ? null : Number(eng.amount_cents || 0));
        if (eng.methods_accepted?.length === 1) setSelectedMethod(eng.methods_accepted[0]);
      } catch {
        setError('Unable to load payment details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchEngagement();
  }, [token]);

  /* --- Plaid callback --- */
  const handlePlaidLinked = useCallback((data: {
    routing_number: string;
    account_number: string;
    account_mask: string;
    account_type: string;
    institution_name: string;
  }) => {
    setAchRouting(data.routing_number);
    setAchAccountNumber(data.account_number);
    setAchAccountType((data.account_type === 'savings' ? 'savings' : 'checking'));
    setAchInstitutionName(data.institution_name);
    setAchMask(data.account_mask);
    setAchLinkedViaPlaid(true);
    setShowManualEntry(false);
  }, []);

  /* --- Submit --- */
  const handleSubmit = async () => {
    if (!engagement || !selectedMethod || !isAmountSelected) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const body: any = {
        payer_name: payerName,
        payer_email: payerEmail,
        payer_phone: payerPhone || undefined,
        method: selectedMethod,
        selected_amount_cents: hasAmountOptions ? paymentAmountCents : undefined,
        notes: notes || undefined,
      };
      if (selectedMethod === 'ach') {
        body.ach_routing = achRouting;
        body.ach_account_number = achAccountNumber;
        body.ach_account_type = achAccountType;
      }
      if (selectedMethod === 'usdc') {
        body.chain = 'base'; // Default to Base; Coinbase Commerce handles network selection
      }
      // Attach legal acceptances — server-side gating enforces these
      body.legal_acceptances = {
        terms: acceptTerms,
        risk_disclosure: acceptRiskDisclosure,
        ...(selectedMethod === 'ach' ? { ach_authorization: acceptAchAuth } : {}),
        ...(selectedMethod === 'usdc' ? { crypto_irrevocable: acceptCryptoIrrevocable } : {}),
      };
      // Attach document signatures if engagement requires signing
      if (engagement.requires_signature && signedDocIds.length > 0) {
        body.document_signatures = signedDocIds;
      }
      const res = await fetch(`${config.apiBaseUrl}/pay/${token}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        if (data.code === 'LEGAL_ACCEPTANCE_REQUIRED') {
          setSubmitError(`Please accept all required agreements: ${(data.missing || []).join(', ')}`);
        } else if (data.code === 'DOCUMENT_SIGNATURE_REQUIRED') {
          setSubmitError(`Please sign all required documents before proceeding.`);
          setStep('documents');
        } else {
          setSubmitError(data.message || 'Payment failed. Please try again.');
        }
        return;
      }
      setTxnResult(data.data);
      // If Coinbase Commerce hosted URL is returned, redirect to it
      if (data.data.hosted_url) {
        window.location.href = data.data.hosted_url;
        return;
      }
      setStep('success');
    } catch {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const copyTxnId = () => {
    if (txnResult?.txn_id) {
      navigator.clipboard.writeText(txnResult.txn_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  /* --- Validation --- */
  const isInfoValid = payerName.trim().length >= 2 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payerEmail) && payerPhone.replace(/\D/g, '').length >= 10;
  const isAchValid = achRouting.length === 9 && achAccountNumber.length >= 4;
  const isUsdcValid = true; // USDC goes straight to Coinbase hosted checkout
  const isDetailsValid = (selectedMethod === 'ach' ? isAchValid : isUsdcValid) && isAmountSelected;
  const legalAcceptancesValid = acceptTerms && acceptRiskDisclosure &&
    (selectedMethod === 'ach' ? acceptAchAuth : true) &&
    (selectedMethod === 'usdc' ? acceptCryptoIrrevocable : true);
  const canContinueFromMethod = !!selectedMethod && isAmountSelected;

  // Document signing validation
  const hasDocsToSign = engagement?.requires_signature && (engagement?.engagement_documents?.length || 0) > 0;
  const allDocsSigned = hasDocsToSign
    ? engagement!.engagement_documents!.every(d => signedDocIds.includes(d.id))
    : true;

  // Helper to determine next step from details
  const nextStepAfterDetails = hasDocsToSign ? 'documents' : 'confirm';
  const prevStepFromConfirm = hasDocsToSign ? 'documents' : 'details';

  /* --- Loading / Error --- */
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto" />
          <p className="text-sm text-gray-500 mt-3">Loading payment details...</p>
        </div>
      </div>
    );
  }

  if (error || !engagement) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Payment Unavailable</h1>
          <p className="text-gray-500 text-sm">{error || 'This payment link is no longer valid.'}</p>
        </div>
      </div>
    );
  }

  /* --- Active transaction: block re-submission --- */
  if (activeTransaction) {
    const statusColor = activeTransaction.status === 'processing' ? 'blue' : 'amber';
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-8">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-[hsl(var(--dark-bg))] px-6 py-6 text-center">
              {logoLight && (
                <img src={logoLight} alt={brandName} className="h-8 mx-auto mb-3 object-contain" />
              )}
              <p className="text-xs text-[hsl(var(--dark-foreground))]/60 uppercase tracking-wider mb-1">Payment Request</p>
              <p className="text-3xl font-bold text-[hsl(var(--dark-foreground))]">
                {hasAmountOptions && !selectedAmountCents ? 'Choose Amount' : fmtCurrency(paymentAmountCents)}
              </p>
              <p className="text-sm text-[hsl(var(--dark-foreground))]/60 mt-1">{engagement.title}</p>
            </div>
            <div className="p-6 text-center">
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-${statusColor}-50 text-${statusColor}-700 mb-4`}>
                <Clock className="h-4 w-4" />
                <span className="text-sm font-medium capitalize">{activeTransaction.status}</span>
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Payment Already Submitted</h2>
              <p className="text-sm text-gray-500 mb-5">
                A {activeTransaction.method.toUpperCase()} payment of{' '}
                <span className="font-semibold text-gray-700">{fmtCurrency(activeTransaction.amount_cents)}</span>{' '}
                was submitted on{' '}
                {new Date(activeTransaction.created_at).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
                })}{' '}
                and is currently being processed.
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-5">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Transaction ID</p>
                <p className="text-sm font-mono text-gray-700">{activeTransaction.txn_id}</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <strong>Need to make changes?</strong> Contact the administrator to cancel this transaction before submitting a new payment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const methods = engagement.methods_accepted || [];
  const methodLabels: Record<string, string> = { ach: 'Bank Transfer (ACH)', usdc: 'USDC (Crypto)' };
  const steps: Step[] = selectedMethod === 'usdc' ? ['method', 'info'] : ['method', 'info', 'details', 'confirm'];
  const currentStepIdx = steps.indexOf(step);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-8">
      <div className="max-w-lg w-full">
        {/* Amount header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-4">
          <div className="bg-[hsl(var(--dark-bg))] px-6 py-6 text-center">
            {logoLight && (
              <img src={logoLight} alt={brandName} className="h-8 mx-auto mb-3 object-contain" />
            )}
            <p className="text-xs text-[hsl(var(--dark-foreground))]/60 uppercase tracking-wider mb-1">Payment Request</p>
            <p className="text-3xl font-bold text-[hsl(var(--dark-foreground))]">
              {hasAmountOptions && !selectedAmountCents ? 'Choose Amount' : fmtCurrency(paymentAmountCents)}
            </p>
            <p className="text-sm text-[hsl(var(--dark-foreground))]/60 mt-1">{engagement.title}</p>
            {engagement.type === 'recurring' && engagement.frequency && (
              <span className="inline-flex items-center gap-1 mt-2 px-3 py-1 bg-white/10 rounded-full text-xs text-[hsl(var(--dark-foreground))]/50">
                <Clock className="h-3 w-3" /> Recurring &mdash; {engagement.frequency}
              </span>
            )}
          </div>
        </div>

        {/* Progress steps */}
        {step !== 'success' && (
          <div className="flex items-center justify-center gap-2 mb-6">
            {steps.map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
                  i < currentStepIdx ? 'bg-primary text-primary-foreground' :
                  i === currentStepIdx ? 'bg-primary text-primary-foreground ring-4 ring-primary/20' :
                  'bg-gray-200 text-gray-400'
                }`}>
                  {i < currentStepIdx ? <CheckCircle className="h-4 w-4" /> : i + 1}
                </div>
                {i < steps.length - 1 && (
                  <div className={`w-8 h-0.5 ${i < currentStepIdx ? 'bg-primary' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Step content card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">

          {/* STEP 1: Payment Method */}
          {step === 'method' && (
            <div className="p-6">
              {hasAmountOptions && (
                <div className="mb-6">
                  <h2 className="font-semibold text-gray-900 text-lg mb-1">Select Amount</h2>
                  <p className="text-sm text-gray-500 mb-4">Choose how much you'd like to pay</p>
                  <div className="grid grid-cols-2 gap-3">
                    {amountOptionsCents.map(amount => (
                      <button
                        key={amount}
                        onClick={() => setSelectedAmountCents(amount)}
                        className={`h-14 rounded-lg border-2 text-base font-semibold transition-all ${
                          selectedAmountCents === amount
                            ? 'border-primary bg-primary-50 text-primary'
                            : 'border-gray-200 text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {fmtCurrency(amount)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <h2 className="font-semibold text-gray-900 text-lg mb-1">Select Payment Method</h2>
              <p className="text-sm text-gray-500 mb-5">Choose how you'd like to pay</p>
              <div className="grid gap-3 mb-6">
                {methods.map(m => (
                  <button key={m} onClick={() => setSelectedMethod(m)}
                    className={`flex items-center gap-3 w-full p-4 rounded-lg border-2 transition-all text-left ${
                      selectedMethod === m ? 'border-primary bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                    <div className={`flex-shrink-0 ${selectedMethod === m ? 'text-primary' : 'text-gray-400'}`}>
                      {m === 'ach' ? <CreditCard className="h-5 w-5" /> : <Wallet className="h-5 w-5" />}
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium text-sm ${selectedMethod === m ? 'text-primary' : 'text-gray-700'}`}>
                        {methodLabels[m] || m.toUpperCase()}
                      </p>
                      <p className="text-xs text-gray-400">
                        {m === 'ach' ? 'Securely connect your bank via Plaid' : 'Pay using USDC stablecoin'}
                      </p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedMethod === m ? 'border-primary' : 'border-gray-300'
                    }`}>
                      {selectedMethod === m && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                    </div>
                  </button>
                ))}
              </div>
              <button disabled={!canContinueFromMethod} onClick={() => setStep('info')}
                className={`w-full py-3 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-all ${
                  canContinueFromMethod ? 'bg-primary hover:bg-primary-600 text-primary-foreground' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}>
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* STEP 2: Payer Info */}
          {step === 'info' && (
            <div className="p-6">
              <h2 className="font-semibold text-gray-900 text-lg mb-1">Your Information</h2>
              <p className="text-sm text-gray-500 mb-5">Enter your details to proceed with payment</p>
              <div className="space-y-4 mb-6">
                <div>
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                    <User className="h-3.5 w-3.5 text-gray-400" /> Full Name <span className="text-red-500">*</span>
                  </label>
                  <input type="text" value={payerName} onChange={e => setPayerName(e.target.value)}
                    placeholder="John Doe"
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none transition" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                    <Mail className="h-3.5 w-3.5 text-gray-400" /> Email Address <span className="text-red-500">*</span>
                  </label>
                  <input type="email" value={payerEmail} onChange={e => setPayerEmail(e.target.value)}
                    placeholder="john@example.com"
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none transition" />
                  <p className="text-xs text-gray-400 mt-1">You'll receive a confirmation receipt at this address</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                    <Phone className="h-3.5 w-3.5 text-gray-400" /> Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input type="tel" value={payerPhone} onChange={e => setPayerPhone(e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none transition" />
                  <p className="text-xs text-gray-400 mt-1">Required for transaction verification</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                    <FileText className="h-3.5 w-3.5 text-gray-400" /> Notes
                  </label>
                  <textarea value={notes} onChange={e => setNotes(e.target.value)}
                    placeholder="Optional notes or reference number"
                    rows={2}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none transition resize-none" />
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setStep('method')}
                  className="px-4 py-3 rounded-lg border border-gray-300 text-gray-600 text-sm font-medium hover:bg-gray-50 flex items-center gap-1">
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                {selectedMethod === 'usdc' ? (
                  <>
                    {/* Legal Acceptance Checkboxes — USDC */}
                    <div className="hidden" />
                  </>
                ) : null}
                {selectedMethod === 'usdc' ? null : (
                  <button disabled={!isInfoValid} onClick={() => setStep('details')}
                    className={`flex-1 py-3 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-all ${
                      isInfoValid ? 'bg-primary hover:bg-primary-600 text-primary-foreground' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}>
                    Continue <ArrowRight className="h-4 w-4" />
                  </button>
                )}
              </div>
              {/* Legal Acceptances for USDC — positioned after the gap so full-width */}
              {selectedMethod === 'usdc' && (
                <div className="mt-5">
                  <div className="border border-gray-200 rounded-lg p-4 mb-4 space-y-3">
                    <p className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                      <Shield className="h-4 w-4 text-gray-400" /> Required Agreements
                    </p>
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input type="checkbox" checked={acceptTerms} onChange={e => setAcceptTerms(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
                      <span className="text-sm text-gray-600 group-hover:text-gray-800">
                        I have read and agree to the <strong>Terms of Service</strong> and <strong>Privacy Policy</strong>.
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input type="checkbox" checked={acceptRiskDisclosure} onChange={e => setAcceptRiskDisclosure(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
                      <span className="text-sm text-gray-600 group-hover:text-gray-800">
                        I acknowledge the <strong>Risk Disclosures</strong> associated with this transaction.
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input type="checkbox" checked={acceptCryptoIrrevocable} onChange={e => setAcceptCryptoIrrevocable(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
                      <span className="text-sm text-gray-600 group-hover:text-gray-800">
                        I understand that <strong>cryptocurrency transactions are irreversible</strong> and cannot be refunded once confirmed on-chain.
                      </span>
                    </label>
                  </div>
                  {submitError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-red-700">{submitError}</p>
                    </div>
                  )}
                  <button disabled={!isInfoValid || !legalAcceptancesValid || !isAmountSelected || submitting} onClick={handleSubmit}
                    className={`w-full py-3 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-all ${
                      isInfoValid && legalAcceptancesValid && isAmountSelected && !submitting ? 'bg-primary hover:bg-primary-600 text-primary-foreground' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}>
                    {submitting ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Connecting to Coinbase...</>
                    ) : (
                      <>Pay {fmtCurrency(paymentAmountCents)} with USDC <ArrowRight className="h-4 w-4" /></>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* STEP 3: Payment Details */}
          {step === 'details' && (
            <div className="p-6">
              <h2 className="font-semibold text-gray-900 text-lg mb-1">
                {selectedMethod === 'ach' ? 'Bank Account' : 'USDC Payment'}
              </h2>
              <p className="text-sm text-gray-500 mb-5">
                {selectedMethod === 'ach'
                  ? 'Securely connect your bank account for the ACH transfer'
                  : "Enter your wallet address and choose your network (Base or Ethereum)"}
              </p>

              {/* --- ACH: Plaid-first flow --- */}
              {selectedMethod === 'ach' && (
                <div className="space-y-4 mb-6">
                  {/* Already linked via Plaid */}
                  {achLinkedViaPlaid ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-green-900">{achInstitutionName}</p>
                          <p className="text-xs text-green-700 font-mono">
                            {achAccountType.charAt(0).toUpperCase() + achAccountType.slice(1)} ****{achMask || achAccountNumber.slice(-4)}
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            setAchLinkedViaPlaid(false);
                            setAchRouting('');
                            setAchAccountNumber('');
                            setAchInstitutionName('');
                            setAchMask('');
                          }}
                          className="text-xs text-green-700 hover:text-green-900 underline"
                        >
                          Change
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Plaid Link button — primary option */}
                      <PlaidCheckoutLink onAccountLinked={handlePlaidLinked} />

                      <div className="relative flex items-center gap-3 py-1">
                        <div className="flex-1 h-px bg-gray-200" />
                        <span className="text-xs text-gray-400 uppercase tracking-widest">or</span>
                        <div className="flex-1 h-px bg-gray-200" />
                      </div>

                      {/* Manual entry — secondary fallback */}
                      <button
                        onClick={() => setShowManualEntry(!showManualEntry)}
                        className="w-full flex items-center justify-between py-2.5 px-4 rounded-lg border border-gray-200 text-sm text-gray-500 hover:bg-gray-50 transition"
                      >
                        <span className="flex items-center gap-2">
                          <Lock className="h-3.5 w-3.5" />
                          Enter bank details manually
                        </span>
                        {showManualEntry ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>

                      {showManualEntry && (
                        <div className="space-y-3 pt-1 pl-2 border-l-2 border-gray-100">
                          <div>
                            <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                              Routing Number <span className="text-red-500">*</span>
                            </label>
                            <input type="text" value={achRouting}
                              onChange={e => setAchRouting(e.target.value.replace(/\D/g, '').slice(0, 9))}
                              placeholder="021000021" maxLength={9}
                              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm font-mono focus:ring-2 focus:ring-primary focus:border-primary outline-none transition" />
                            <p className="text-xs text-gray-400 mt-1">9-digit ABA routing number</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                              Account Number <span className="text-red-500">*</span>
                            </label>
                            <input type="password" value={achAccountNumber}
                              onChange={e => setAchAccountNumber(e.target.value.replace(/\D/g, '').slice(0, 17))}
                              placeholder="Your account number" maxLength={17}
                              autoComplete="off"
                              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm font-mono focus:ring-2 focus:ring-primary focus:border-primary outline-none transition" />
                            <p className="text-xs text-gray-400 mt-1">4-17 digits, transmitted securely</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-700 mb-1.5 block">Account Type</label>
                            <div className="flex gap-3">
                              {(['checking', 'savings'] as const).map(t => (
                                <button key={t} onClick={() => setAchAccountType(t)}
                                  className={`flex-1 py-2 rounded-lg border-2 text-sm font-medium capitalize transition-all ${
                                    achAccountType === t ? 'border-primary bg-primary-50 text-primary' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                                  }`}>{t}</button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Security note */}
                  <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <Shield className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-primary-800">Secure ACH Processing</p>
                        <p className="text-xs text-primary-600 mt-1">
                          {achLinkedViaPlaid
                            ? 'Your bank account was verified securely through Plaid. Only the last 4 digits are stored for your records.'
                            : 'Your bank details are transmitted to our ACH processor (Increase) via encrypted connection. Only the last 4 digits are retained.'
                          }
                          {' '}Funds will be debited in 2-3 business days.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* --- USDC flow --- */}
              {selectedMethod === 'usdc' && (
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                      Your Wallet Address <span className="text-red-500">*</span>
                    </label>
                    <input type="text" value={walletAddress} onChange={e => setWalletAddress(e.target.value.trim())}
                      placeholder="0x742d35Cc6634C0532925a3b844Bc9e..."
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm font-mono focus:ring-2 focus:ring-primary focus:border-primary outline-none transition" />
                    <p className="text-xs text-gray-400 mt-1">The wallet address you'll send USDC from (for tracking)</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">Network</label>
                    <div className="grid grid-cols-2 gap-2">
                      {([['base', 'Base (Recommended)'], ['ethereum', 'Ethereum']] as const).map(([id, label]) => (
                        <button key={id} onClick={() => setChain(id)}
                          className={`py-2 rounded-lg border-2 text-xs font-medium transition-all ${
                            chain === id ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                          }`}>{label}</button>
                      ))}
                    </div>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <Wallet className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-purple-800">USDC Payment</p>
                        <p className="text-xs text-purple-600 mt-1">
                          After submitting, you'll see the destination wallet address to send your USDC to.
                          Send exactly {fmtCurrency(paymentAmountCents)} in USDC on the selected network.
                          Only <strong>Base</strong> and <strong>Ethereum</strong> are accepted.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button onClick={() => setStep('info')}
                  className="px-4 py-3 rounded-lg border border-gray-300 text-gray-600 text-sm font-medium hover:bg-gray-50 flex items-center gap-1">
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <button disabled={!isDetailsValid} onClick={() => setStep(nextStepAfterDetails as Step)}
                  className={`flex-1 py-3 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-all ${
                    isDetailsValid ? 'bg-primary hover:bg-primary-600 text-primary-foreground' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}>
                  {hasDocsToSign ? 'Review Documents' : 'Review Payment'} <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* STEP: Document Signing */}
          {step === 'documents' && engagement?.engagement_documents && (
            <div className="p-6">
              <h2 className="font-semibold text-gray-900 text-lg mb-1">Review & Sign Documents</h2>
              <p className="text-sm text-gray-500 mb-5">
                Please review and acknowledge each document below before proceeding to payment.
              </p>

              <div className="space-y-3 mb-6">
                {engagement.engagement_documents.map(doc => {
                  const isSigned = signedDocIds.includes(doc.id);
                  return (
                    <div key={doc.id} className={`border rounded-lg p-4 transition-colors ${isSigned ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}>
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          <FileText className={`h-5 w-5 ${isSigned ? 'text-green-600' : 'text-gray-400'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm">{doc.title}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {doc.original_filename} • {doc.category} • {((doc.file_size_bytes || 0) / 1024).toFixed(0)} KB
                          </p>
                        </div>
                        {isSigned && <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />}
                      </div>
                      <div className="mt-3 flex items-center gap-3">
                        <a
                          href={`${config.apiBaseUrl}/pay/${token}/document/${doc.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:text-primary-700 underline"
                        >
                          View Document
                        </a>
                        {!isSigned ? (
                          <button
                            onClick={() => setSignedDocIds(prev => [...prev, doc.id])}
                            className="text-xs px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary-600 font-medium"
                          >
                            I have read and agree to sign
                          </button>
                        ) : (
                          <span className="text-xs text-green-700 font-medium">✓ Signed</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-3">
                <button onClick={() => setStep('details')}
                  className="px-4 py-3 rounded-lg border border-gray-300 text-gray-600 text-sm font-medium hover:bg-gray-50 flex items-center gap-1">
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <button disabled={!allDocsSigned} onClick={() => setStep('confirm')}
                  className={`flex-1 py-3 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-all ${
                    allDocsSigned ? 'bg-primary hover:bg-primary-600 text-primary-foreground' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}>
                  Review Payment <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 4: Confirm & Submit */}
          {step === 'confirm' && (
            <div className="p-6">
              <h2 className="font-semibold text-gray-900 text-lg mb-1">Confirm Payment</h2>
              <p className="text-sm text-gray-500 mb-5">Review the details below before submitting</p>

              <div className="bg-gray-50 rounded-lg border border-gray-200 divide-y divide-gray-200 mb-6">
                <div className="px-4 py-3 flex justify-between">
                  <span className="text-sm text-gray-500">Amount</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {fmtCurrency(paymentAmountCents)} {engagement.currency || 'USD'}
                  </span>
                </div>
                <div className="px-4 py-3 flex justify-between">
                  <span className="text-sm text-gray-500">Description</span>
                  <span className="text-sm text-gray-900">{engagement.title}</span>
                </div>
                <div className="px-4 py-3 flex justify-between">
                  <span className="text-sm text-gray-500">Method</span>
                  <span className="text-sm text-gray-900 uppercase">{selectedMethod}</span>
                </div>
                <div className="px-4 py-3 flex justify-between">
                  <span className="text-sm text-gray-500">Name</span>
                  <span className="text-sm text-gray-900">{payerName}</span>
                </div>
                <div className="px-4 py-3 flex justify-between">
                  <span className="text-sm text-gray-500">Email</span>
                  <span className="text-sm text-gray-900">{payerEmail}</span>
                </div>
                {payerPhone && (
                  <div className="px-4 py-3 flex justify-between">
                    <span className="text-sm text-gray-500">Phone</span>
                    <span className="text-sm text-gray-900">{payerPhone}</span>
                  </div>
                )}
                {selectedMethod === 'ach' && (
                  <div className="px-4 py-3 flex justify-between">
                    <span className="text-sm text-gray-500">Account</span>
                    <span className="text-sm text-gray-900 font-mono">
                      {achInstitutionName ? `${achInstitutionName} · ` : ''}****{achMask || achAccountNumber.slice(-4)}
                    </span>
                  </div>
                )}
                {selectedMethod === 'ach' && (
                  <div className="px-4 py-3 flex justify-between">
                    <span className="text-sm text-gray-500">Account Type</span>
                    <span className="text-sm text-gray-900 capitalize">{achAccountType}</span>
                  </div>
                )}
                {selectedMethod === 'usdc' && walletAddress && (
                  <div className="px-4 py-3 flex justify-between">
                    <span className="text-sm text-gray-500">Wallet</span>
                    <span className="text-sm text-gray-900 font-mono truncate max-w-[200px]">{walletAddress}</span>
                  </div>
                )}
                {notes && (
                  <div className="px-4 py-3 flex justify-between">
                    <span className="text-sm text-gray-500">Notes</span>
                    <span className="text-sm text-gray-900 max-w-[200px] text-right">{notes}</span>
                  </div>
                )}
              </div>

              {submitError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-red-700">{submitError}</p>
                </div>
              )}

              {/* Legal Acceptance Checkboxes — ACH */}
              <div className="border border-gray-200 rounded-lg p-4 mb-5 space-y-3">
                <p className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <Shield className="h-4 w-4 text-gray-400" /> Required Agreements
                </p>
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input type="checkbox" checked={acceptTerms} onChange={e => setAcceptTerms(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
                  <span className="text-sm text-gray-600 group-hover:text-gray-800">
                    I have read and agree to the <strong>Terms of Service</strong> and <strong>Privacy Policy</strong>.
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input type="checkbox" checked={acceptRiskDisclosure} onChange={e => setAcceptRiskDisclosure(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
                  <span className="text-sm text-gray-600 group-hover:text-gray-800">
                    I acknowledge the <strong>Risk Disclosures</strong> associated with this transaction.
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input type="checkbox" checked={acceptAchAuth} onChange={e => setAcceptAchAuth(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
                  <span className="text-sm text-gray-600 group-hover:text-gray-800">
                    I authorize <strong>{brandName}</strong> (via Increase.com) to initiate a one-time <strong>ACH debit</strong> of <strong>{fmtCurrency(paymentAmountCents)}</strong> from my linked bank account. This authorization remains in effect for this transaction only. I may revoke this authorization by contacting support before the transfer is initiated. I agree that this transaction is subject to <strong>NACHA Operating Rules</strong> and my financial institution's policies.
                  </span>
                </label>
                {/* ACH Same-Day Cutoff Disclaimer */}
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-2">
                  <Clock className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-800 leading-relaxed">
                    <strong>Same-Day ACH Notice:</strong> ACH transactions initiated before <strong>4:45 PM ET</strong> on a business day are eligible for same-day processing. Transactions submitted after this cutoff or on weekends/holidays will be processed on the next business day.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setStep(prevStepFromConfirm as Step)} disabled={submitting}
                  className="px-4 py-3 rounded-lg border border-gray-300 text-gray-600 text-sm font-medium hover:bg-gray-50 flex items-center gap-1">
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <button onClick={handleSubmit} disabled={submitting || !legalAcceptancesValid || !isAmountSelected}
                  className={`flex-1 py-3 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-all ${
                    !submitting && legalAcceptancesValid && isAmountSelected ? 'bg-primary hover:bg-primary-600 text-primary-foreground' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}>
                  {submitting ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Processing...</>
                  ) : (
                    <>Submit Payment {fmtCurrency(paymentAmountCents)}</>
                  )}
                </button>
              </div>

              <p className="text-xs text-gray-400 text-center mt-4 flex items-center justify-center gap-1">
                <Shield className="h-3 w-3" /> Your information is encrypted and secure
              </p>
            </div>
          )}

          {/* SUCCESS */}
          {step === 'success' && txnResult && (
            <div className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Payment Submitted!</h2>
              <p className="text-sm text-gray-500 mb-6">
                Your payment of{' '}
                <span className="font-semibold text-gray-900">{fmtCurrency(txnResult.amount_cents)}</span>{' '}
                has been submitted and is being processed.
              </p>

              {/* USDC destination address */}
              {txnResult.method === 'usdc' && txnResult.destination_address && (
                <div className="bg-purple-50 border-2 border-purple-300 rounded-xl p-5 mb-5 text-left">
                  <p className="text-xs text-purple-600 uppercase tracking-wider font-bold mb-2">Send USDC to this address</p>
                  <div className="bg-white rounded-lg px-4 py-3 border border-purple-200 flex items-center gap-2">
                    <p className="text-sm font-mono font-semibold text-gray-900 break-all flex-1">{txnResult.destination_address}</p>
                    <button onClick={() => { navigator.clipboard.writeText(txnResult.destination_address); }}
                      className="flex-shrink-0 text-xs text-purple-600 hover:text-purple-700 font-medium">
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-3 flex items-center gap-4">
                    <div>
                      <p className="text-xs text-purple-500">Network</p>
                      <p className="text-sm font-semibold text-purple-800 capitalize">{txnResult.destination_network}</p>
                    </div>
                    <div>
                      <p className="text-xs text-purple-500">Amount</p>
                      <p className="text-sm font-semibold text-purple-800">{fmtCurrency(txnResult.amount_cents)} USDC</p>
                    </div>
                  </div>
                  <p className="text-xs text-purple-500 mt-3">Send <strong>exactly</strong> the amount above in USDC. Sending on the wrong network or token may result in loss of funds.</p>
                </div>
              )}

              <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 mb-6 text-left">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-gray-500 uppercase tracking-wide">Transaction ID</span>
                  <button onClick={copyTxnId}
                    className="flex items-center gap-1 text-xs text-primary hover:text-primary-600">
                    {copied ? <><Check className="h-3 w-3" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
                  </button>
                </div>
                <p className="text-sm font-mono font-medium text-gray-900">{txnResult.txn_id}</p>

                <div className="grid grid-cols-2 gap-3 mt-4 pt-3 border-t border-gray-200">
                  <div>
                    <p className="text-xs text-gray-500">Method</p>
                    <p className="text-sm text-gray-900 uppercase">{txnResult.method}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Status</p>
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-amber-600">
                      <Clock className="h-3.5 w-3.5" /> {txnResult.status === 'processing' ? 'Processing' : 'Pending'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Investment / Payment Documents */}
              {engagement.engagement_documents && engagement.engagement_documents.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm font-medium text-blue-900 flex items-center gap-1.5 mb-1">
                    <FileText className="h-4 w-4" /> Transaction Documents
                  </p>
                  <p className="text-xs text-blue-700 mb-3">
                    Please download and retain these documents for your records, bookkeeping, and financial reporting.
                  </p>
                  <div className="space-y-1.5">
                    {engagement.engagement_documents.map(doc => (
                      <a
                        key={doc.id}
                        href={`${config.apiBaseUrl}/documents/deal/${doc.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-xs text-blue-800 hover:text-blue-900 hover:bg-blue-100 rounded-md px-2 py-1.5 transition-colors"
                      >
                        <FileText className="h-3.5 w-3.5 flex-shrink-0" />
                        <span className="truncate flex-1">{doc.title || doc.original_filename}</span>
                        <span className="text-blue-500 text-[10px] uppercase">{doc.category}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <p className="text-sm text-gray-500">
                A confirmation email has been sent to{' '}
                <span className="font-medium text-gray-700">{payerEmail}</span>
              </p>
              <p className="text-xs text-gray-400 mt-2">
                {selectedMethod === 'ach'
                  ? 'Your bank account will be debited via ACH within 2-3 business days.'
                  : 'Send your USDC to the destination address shown above. Your payment will be confirmed once the on-chain transaction is verified.'}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
            <CheckCircle className="h-3.5 w-3.5 text-green-500" />
            Secured by
            {tenant?.branding?.logoPrimaryPath ? (
              <img src={tenant.branding.logoPrimaryPath} alt={brandName} className="h-4 object-contain" />
            ) : (
              <span className="font-medium">{brandName}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentLink;
